import { API_BASE_URL } from 'configs/AppConfig';
import {
  CREATE_API_PROGRESS,
  mapProgressRange,
  uploadFileWithMappedProgress,
  UPLOAD_PROGRESS_MAX,
} from 'helpers/publishProgressUtils';
import { uploadFilesWithProgress } from 'utils/uploadFilesWithProgress';

export const isEditJob = (job) => job?.mode === 'EDIT' && Boolean(job?.recordId);

export const getSavePhase = (job) => (isEditJob(job) ? 'updating' : 'creating');

export const reportPublishProgress = (onProgress, phase, percent) => {
  onProgress?.({
    phase,
    percent,
    status: 'processing',
  });
};

export const uploadPublishImage = async ({
  file,
  existingUrl,
  accessToken,
  signal,
  onProgress,
  phase,
  rangeStart,
  rangeEnd,
  thumbnailRequired,
}) => {
  if (file) {
    reportPublishProgress(onProgress, phase, rangeStart);

    return uploadFileWithMappedProgress({
      file,
      accessToken,
      thumbnailRequired,
      rangeStart,
      rangeEnd,
      onProgress: ({ percent }) => {
        reportPublishProgress(onProgress, phase, percent);
      },
      signal,
    });
  }

  return existingUrl || null;
};

/** Upload (or keep) image and return original + thumbnail meta. */
export const uploadPublishImageAsset = async ({
  file,
  existingUrl,
  existingThumbnail,
  accessToken,
  signal,
  onProgress,
  phase,
  rangeStart,
  rangeEnd,
  thumbnailRequired = 'Yes',
}) => {
  const wantsThumbnail =
    String(thumbnailRequired || '').toLowerCase() === 'yes';

  if (file) {
    reportPublishProgress(onProgress, phase, rangeStart);

    const urls = await uploadFilesWithProgress({
      files: [file],
      accessToken,
      thumbnailRequired,
      onProgress: ({ percent }) => {
        reportPublishProgress(
          onProgress,
          phase,
          mapProgressRange(rangeStart, rangeEnd, percent),
        );
      },
      signal,
    });

    const item = urls?.[0];
    const original =
      typeof item === 'string' ? item : item?.original || null;
    if (!original) {
      throw new Error('Upload failed');
    }

    const thumbnailFromUpload =
      typeof item === 'object'
        ? item?.thumbnail ||
          item?.thumb ||
          item?.thumbnailUrl ||
          item?.thumbUrl ||
          null
        : null;

    // Pages API requires thumbnail; filemanager sometimes returns null (e.g. webp).
    const thumbnail = thumbnailFromUpload || (wantsThumbnail ? original : null);

    if (wantsThumbnail && !thumbnail) {
      throw new Error('Image thumbnail is required');
    }

    return {
      url: original,
      thumbnail,
    };
  }

  if (existingUrl) {
    const thumbnail =
      existingThumbnail || (wantsThumbnail ? existingUrl : null);
    if (wantsThumbnail && !thumbnail) {
      throw new Error('Image thumbnail is required');
    }
    return {
      url: existingUrl,
      thumbnail,
    };
  }

  return null;
};

export const submitPublishRequest = async ({
  job,
  createPath,
  updatePath,
  payload,
  accessToken,
  signal,
  onProgress,
  entityLabel,
  uploadPercent = UPLOAD_PROGRESS_MAX,
  updateMethod = 'PUT',
}) => {
  const editing = isEditJob(job);
  const savePhase = getSavePhase(job);

  reportPublishProgress(onProgress, savePhase, uploadPercent);
  reportPublishProgress(onProgress, savePhase, CREATE_API_PROGRESS);

  const baseUrl = API_BASE_URL.replace(/\/+$/, '');
  const response = await fetch(
    editing
      ? `${baseUrl}${updatePath(job.recordId)}`
      : `${baseUrl}${createPath}`,
    {
      method: editing ? updateMethod : 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
      signal,
    },
  );

  const data = await response.json();

  if (data.statusCode === 401 || response.status === 401) {
    const error = new Error(data.message || 'Unauthorized');
    error.statusCode = 401;
    throw error;
  }

  if (!response.ok) {
    const message = Array.isArray(data.message)
      ? data.message.join(', ')
      : data.message ||
        (editing
          ? `Failed to update ${entityLabel}.`
          : `Failed to create ${entityLabel}.`);
    throw new Error(message);
  }

  reportPublishProgress(onProgress, 'complete', 100);

  return data;
};
